
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import PanelFrame from '@/components/molecules/PanelFrame.vue'
import TextSearch from '@/components/molecules/TextSearch.vue'
import ButtonUpload from '@/components/molecules/ButtonUpload.vue'
import ButtonDownload from '@/components/molecules/ButtonDownload.vue'
import RoundSelect from '@/components/molecules/RoundSelect.vue'
import StudentListTable from '@/components/organisms/manage/StudentListTable.vue'
import PageLinkButton from '@/components/organisms/PageLinkButton.vue'
import { StudentList } from '@/models/manage/StudentList'
import { SelectItem } from '@/models/SelectItem'

/**
 * 生徒一覧パネル
 */
@Component({
  components: {
    PanelFrame,
    TextSearch,
    ButtonUpload,
    ButtonDownload,
    RoundSelect,
    StudentListTable,
    PageLinkButton,
  },
})
export default class StudentListPanel extends Vue {
  /** ユーザのアクティベート状態フィルター */
  @Ref()
  userActivateStatusFilter!: RoundSelect

  /** 学年フィルター */
  @Ref()
  gradeFilter!: RoundSelect

  /** 教室フィルター */
  @Ref()
  branchFilter!: RoundSelect

  /** コースフィルター */
  @Ref()
  courseFilter!: RoundSelect

  /** クラスフィルター */
  @Ref()
  classFilter!: RoundSelect

  /** 検索欄 */
  @Ref()
  textSearch!: TextSearch

  /** 生徒一覧テーブル */
  @Ref()
  studentListTable!: StudentListTable

  /** 塾権限での使用か否か(機能制限用) */
  @Prop()
  private isAcademy!: boolean

  /** 生徒情報一覧 */
  @Prop()
  private studentList!: StudentList[]

  /** ユーザのアクティベート状態フィルターの選択肢 */
  @Prop()
  private filterUserActivateStatusList!: SelectItem[]

  /** ユーザのアクティベート状態フィルターの初期選択値 */
  @Prop()
  private defaultUserActivateStatus!: string

  /** 学年フィルターの選択肢 */
  @Prop()
  private filterGradeList!: SelectItem[]

  /** 学年フィルターの初期選択値 */
  @Prop()
  private defaultGardeCode!: string

  /** 教室フィルターの選択肢 */
  @Prop()
  private filterBranchList?: SelectItem[]

  /** コースフィルターの選択肢 */
  @Prop()
  private filterAcademyCourseList!: SelectItem[]

  /** クラスフィルターの選択肢 */
  @Prop()
  private filterAcademyClassList!: SelectItem[]

  /** クラスフィルターのdisabled制御 */
  @Prop()
  private isClassFilterDisabled!: boolean

  /** ページ数 */
  @Prop()
  private pageLength!: number

  /** 現在のページ */
  @Prop()
  private currentPage!: number

  /** ユーザのアクティベート状態フィルター変更時のイベント */
  @Prop()
  private changeUserActivateStatusFilter!: () => Promise<void>

  /** 学年フィルター変更時のイベント */
  @Prop()
  private changeGradeFilter!: () => Promise<void>

  /** 教室フィルター変更時のイベント */
  @Prop()
  private changeBranchFilter?: () => Promise<void>

  /** コースフィルター変更時のイベント */
  @Prop()
  private changeCourseFilter!: () => Promise<void>

  /** クラスフィルター変更時のイベント */
  @Prop()
  private changeClassFilter!: () => Promise<void>

  /** 検索ボタン押下時の処理 */
  @Prop()
  private eventClickSearch!: () => Promise<void>

  /** アップロードボタン押下時のイベント */
  @Prop()
  private eventClickUpload?: () => void

  /** ダウンロードボタン押下時のイベント */
  @Prop()
  private eventClickDownload!: () => Promise<void>

  /** 編集エリア表示イベント */
  @Prop()
  private eventClickEditButton?: (
    event: Event,
    userId: number,
    lockedStatus: string,
    registerType: number | null
  ) => Promise<void>

  /** ページリンクボタン押下時のイベント */
  @Prop()
  private changePage!: (value: number) => Promise<void>

  /**
   * 検索文字列
   *
   * @returns 入力された文字列
   */
  public textInputed(): string {
    return this.textSearch.text
  }

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  public currentGrade(): string {
    return this.gradeFilter.selectedValue()
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード
   */
  public currentBranch(): string {
    return this.branchFilter.selectedValue()
  }

  /**
   * 教室選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentBranchValue(value: string): void {
    this.branchFilter.setCurrentValue(value)
  }

  /**
   * 選択中のコース
   *
   * @returns コースコード
   */
  public currentCourse(): string {
    return this.courseFilter.selectedValue()
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentCourseValue(value: string): void {
    this.courseFilter.setCurrentValue(value)
  }

  /**
   * 選択中のクラス
   *
   * @returns クラスコード
   */
  public currentClass(): string {
    return this.classFilter.selectedValue()
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentClassValue(value: string): void {
    this.classFilter.setCurrentValue(value)
  }

  /**
   * チェック済みの生徒ID一覧
   *
   * @returns 生徒ID一覧
   */
  public checkedStudentList(): number[] {
    return this.studentListTable.checkedStudentList
  }

  /**
   * 選択済生徒リストのクリア
   */
  public clearSelectedStudents(): void {
    this.studentListTable.clearSelectedStudents()
  }
}
